'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import { getOrders } from '../../common/gqlActions/getOrders.action';
import { OrderStatus, OrdersViewQuery$data } from '@bts-web/data-layer/server';
import { useHandleGqlErrorsWithSnackbar } from '@bts-web/core-features/common';
import { ORDERS_LIST_PAGE_SIZE } from '@bts-web/core-features/config';

interface OrdersContextProps {
  orders: OrdersViewQuery$data['orders'];
  selectedFilter: string;
  setSelectedFilter: (filter: OrderStatus) => void;
  isPending: boolean;
  loadMoreOrders: () => void;
}

const OrdersContext = createContext<OrdersContextProps | null>(null);

export function useOrdersContext() {
  const contextValue = useContext(OrdersContext);

  if (contextValue === null) {
    throw new Error(
      'ordersContext must be used within a OrdersContextProvider',
    );
  }

  return contextValue;
}

export const OrdersContextProvider = ({
  children,
  defaultFilter,
  initialData,
}: PropsWithChildren<{
  defaultFilter: string;
  initialData: {
    data: OrdersViewQuery$data;
    errors?: string[];
  };
}>) => {
  const [orders, setOrders] = useState<OrdersViewQuery$data['orders']>(
    initialData.data.orders,
  );

  const [selectedFilter, setSelectedFilter] = useState<OrderStatus>(
    defaultFilter as OrderStatus,
  );

  const [isPending, startTransition] = useTransition();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const isFirstRender = useRef(true);

  const isLoadingMore = useRef(false);

  const loadMoreOrders = async () => {
    if (isLoadingMore.current) return;

    isLoadingMore.current = true;

    const currentPageCursor = orders?.pageInfo?.endCursor;

    try {
      const response = await getOrders({
        status: selectedFilter,
        size: ORDERS_LIST_PAGE_SIZE,
        cursor: currentPageCursor,
      });

      if (response.errors) {
        processErrors(response.errors);
      }

      const loadedOrders = response.data?.orders;

      if (loadedOrders) {
        setOrders(
          (prev) =>
            ({
              ...prev,
              edges: [...(prev?.edges || []), ...(loadedOrders?.edges || [])],
              pageInfo: loadedOrders?.pageInfo,
            }) as OrdersContextProps['orders'],
        );
      }
    } catch (error) {
      if (typeof error === 'string') {
        processErrors([error] as string[]);
      } else {
        processErrors(error as unknown as string[]);
      }
    } finally {
      isLoadingMore.current = false;
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      if (initialData.errors) {
        processErrors(initialData.errors);

        setOrders({
          edges: [],
          totalCount: 0,
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
          },
        });
      }

      return;
    }

    startTransition(async () => {
      try {
        const list = await getOrders({
          status: selectedFilter,
          size: ORDERS_LIST_PAGE_SIZE,
        });

        if (list.errors) {
          processErrors(list.errors);

          setOrders({
            edges: [],
            totalCount: 0,
            pageInfo: {
              endCursor: null,
              hasNextPage: false,
            },
          });
        } else {
          setOrders(list.data.orders);
        }
      } catch (error) {
        processErrors([
          error instanceof Error ? error.message : 'Unknown error',
        ]);
      }
    });
  }, [selectedFilter]);

  return (
    <OrdersContext.Provider
      value={{
        orders,
        selectedFilter,
        setSelectedFilter,
        isPending,
        loadMoreOrders,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};
